import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Container, Card, Grid, Button, Icon} from 'semantic-ui-react';
import {Link} from 'react-router-dom';
import {hasAccessPermission} from '../../common/permissions/validations';
import {scopes, resources} from '../../common/permissions/dictionary';
import {paths} from '../../common/dictionaries/urls';
import {Actions} from '../../common/actions';
import AgenciesTable from './agenciesTable/agenciesTable';
import {getAgenciesColumns} from './agenciesTable/agenciesTableColumns';
import {toUpper} from 'lodash';
import {renderTableLoader} from '../../common/components/tableLoader';
import {downloadFile} from '../../common/util/file';

const {
  navigateTo,
  fecthAgenciesList,
  updateAgency,
  fecthProvincesList,
  fetchDealers,
  exportToExcelAgencies,
  cleanAgenciesUrl,
} = Actions;

const NEW_AGENCY_INITIAL_STATE = {
  'identification': '',
  'code': '',
  'name': '',
  'address': '',
  'phone': '',
  'status': 'A',
  'provinceCode': '',
  'cantonCode': '',
};

const LAST_AGENCY_INITIAL_STATE = {
  'inputId': null,
  'caretPos': 0,
};

const Index = () => {
  const permissions = useSelector((state) => state.session.permissions);
  const loading = useSelector((state) => state.agencies.loading);
  const agenciesList = useSelector((state) => state.agencies.list);
  const provincesList = useSelector((state) => state.provinces.list);
  const exportAgenciesURL = useSelector((state) => state.exportAgencies.url);
  const dealersList = useSelector((state) => {
    return state.dealer.list;
  });
  const [addAgency, setAddAgency] = useState(null);
  const [agencyEditing, setAgencyEditing] = useState(null);
  const [lastAgencyInputChanged, setLastAgencyInputChanged] = useState(
    LAST_AGENCY_INITIAL_STATE,
  );
  let inputRef = useRef();
  const agencyInputsRefs = [];
  const dispatch = useDispatch();

  useEffect(() => {
    if (!hasAccessPermission(permissions, resources.AgenciesManagement, scopes.See) &&
     !hasAccessPermission(permissions, resources.AgenciesManagement, scopes.Edit)) {
      dispatch(navigateTo(paths.AccessDenied));
    }
    dispatch(cleanAgenciesUrl());
    dispatch(fecthAgenciesList());
    dispatch(fecthProvincesList());
    dispatch(fetchDealers('importers'));
  }, []);

  const handleAgencyValue = (e, input) => {
    e.preventDefault();
    const data = {...agencyEditing, [input.id]: toUpper(input.value)};
    setAgencyEditing(data);
    if (input.id === 'provinceCode') {
      const cleanCantonData = {...data, 'cantonCode': ''};
      setAgencyEditing(cleanCantonData);
    }
    if (input.type && input.type === 'text') {
      setLastAgencyInputChanged({'inputId': input.id, 'caretPos': e.target.selectionStart});
    }
  };

  const agencySelectedToEdit = (agency) => {
    if (agencyEditing) {
      return agencyEditing.key === agency.key ? true : false;
    }
    return false;
  };

  const getAgencyEditingValue = (propId) => {
    return agencyEditing[propId];
  };
  
  const setAgencyRef = (input) => {
    inputRef = input;
    agencyInputsRefs.push(inputRef);
  };

  const handleAgencyEdit = (e, agency) => {
    e.preventDefault();
    setAgencyEditing(agency);
  };

  const handleAgencyUpdate = () => {
    dispatch(updateAgency({'data': agencyEditing}));
  };

  const handleAgencyEditCancel = () => {
    setAgencyEditing(null);
    setLastAgencyInputChanged(LAST_AGENCY_INITIAL_STATE);
  };

  useEffect(() => {
    const input = agencyInputsRefs.find((x) => x.props.id === lastAgencyInputChanged.inputId);
    if (input) {
      input.focus();
      input.inputRef.current
        .setSelectionRange(lastAgencyInputChanged.caretPos, lastAgencyInputChanged.caretPos);
    }
  }, [agencyEditing]);

  const changeNewAgencyValue = (e, column, value) => {
    e.preventDefault();
    const reg = new RegExp('[0-9]');
    if (column.id === 'code') {
      if (value && (!reg.test(value) || value.length > 2)) {
        return;
      }
    }
    const data = {...addAgency, [column.id]: toUpper(value)};
    setAddAgency(data);
    if (column.id === 'provinceCode') {
      const cleanCantonData = {...data, 'cantonCode': ''};
      setAddAgency(cleanCantonData);
    }
  };
  
  const saveNewAgency = (e) => {
    e.preventDefault();
    dispatch(updateAgency({'data': addAgency}));
  };

  const cancelAddAgency = () => {
    setAddAgency(null);
  };

  const validateAgency = (type) => {
    if (type === 'add') {
      return addAgency ?
        addAgency.name && addAgency.identification && addAgency.address && addAgency.phone &&
        addAgency.cantonCode && addAgency.provinceCode ? false : true : false;
    }
    return agencyEditing ?
      agencyEditing.name && agencyEditing.address && agencyEditing.phone &&
      agencyEditing.cantonCode && agencyEditing.provinceCode ? false : true : false;
  };

  const addAgencyHandler = (e) => {
    e.preventDefault();
    setAddAgency({...NEW_AGENCY_INITIAL_STATE});
  };
  
  useEffect(() => {
    handleAgencyEditCancel();
    cancelAddAgency();
  }, [agenciesList]);

  const handleAgencyStatus = (e, checkedValue, item) => {
    e.preventDefault();
    dispatch(updateAgency({'data': {...item, 'status': checkedValue ? 'A' : 'I'}}));
  };

  const exportAgenciesHandler = (e) => {
    e.preventDefault();
    dispatch(exportToExcelAgencies());
  };

  useEffect(() => {
    if (exportAgenciesURL) {
      downloadFile(exportAgenciesURL);
      dispatch(cleanAgenciesUrl());
    }
  }, [exportAgenciesURL, dispatch]);

  const agenciesTableColumns = getAgenciesColumns(dealersList, provincesList);

  return (
    <Container fluid>
      <Card raised fluid style={{'backgroundColor': 'transparent'}}>
        <div className='title-bar'>
          <Grid>
            <Grid.Row columns={2}>
              <Grid.Column>
                <div className='title'>
                  <h2>Gestión de Agencias</h2>
                </div>
              </Grid.Column>
              <Grid.Column textAlign='right' className='flex middle end'>
                <Icon className='add-circle' size='big' color='green'
                  link onClick={(e) => addAgencyHandler(e)} />
                <Button secondary size='small' className='add-btns-pading' compact
                  onClick={(e) => exportAgenciesHandler(e)}>
                  <img src={`${process.env.PUBLIC_URL}/icons/excel.svg`} alt='excel icon' width={25} />
                </Button>
                <Link to={paths.TransportistManagement}>
                  <Button primary>Transportistas</Button>
                </Link>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>
      </Card>
      <Card fluid raised style={{'padding': '1em'}}>
        {loading ? renderTableLoader() :
          <AgenciesTable tableColumns={agenciesTableColumns} tableData={agenciesList}
            agencySelectedToEdit={agencySelectedToEdit.bind(this)}
            handleAgencyValue={handleAgencyValue.bind(this)}
            getAgencyEditingValue={getAgencyEditingValue.bind(this)}
            setAgencyRef={setAgencyRef.bind(this)}
            handleAgencyEdit={handleAgencyEdit.bind(this)}
            handleAgencyUpdate={handleAgencyUpdate.bind(this)}
            handleAgencyEditCancel={handleAgencyEditCancel.bind(this)}
            provincesList={provincesList}
            dealersList={dealersList}
            addAgency={addAgency}
            changeNewAgencyValue={changeNewAgencyValue.bind(this)}
            saveNewAgency={saveNewAgency.bind(this)}
            cancelAddAgency={cancelAddAgency.bind(this)}
            validateAgency={validateAgency.bind(this)}
            handleAgencyStatus={handleAgencyStatus.bind(this)} />}
      </Card>
    </Container>
  );
};
 
export default Index;
